// Center coordinates and default zoom level for Swedish cities.
// Used to ensure the city map is centered on the actual city, not on the
// first event in the dataset.

export interface CityCenter {
  lat: number;
  lng: number;
  zoom: number;
}

// Swedish municipalities/cities used by the Polisen feed. When we can't pin
// an event to a specific street/district, we drop the fallback marker on the
// city center listed here. Missing entries fall back to a country-wide center
// which puts pins in random forests — always add the city if you see it in
// the feed without coords.
const CITY_COORDS: Record<string, CityCenter> = {
  // ── Storstäder & Större kommuner ──
  stockholm: { lat: 59.3293, lng: 18.0686, zoom: 11 },
  goteborg: { lat: 57.7089, lng: 11.9746, zoom: 11 },
  malmo: { lat: 55.605, lng: 13.0038, zoom: 12 },
  uppsala: { lat: 59.8586, lng: 17.6389, zoom: 12 },
  vasteras: { lat: 59.6099, lng: 16.5448, zoom: 12 },
  orebro: { lat: 59.2753, lng: 15.2134, zoom: 12 },
  linkoping: { lat: 58.4108, lng: 15.6214, zoom: 12 },
  helsingborg: { lat: 56.0465, lng: 12.6945, zoom: 12 },
  jonkoping: { lat: 57.7826, lng: 14.1618, zoom: 12 },
  norrkoping: { lat: 58.5877, lng: 16.1924, zoom: 12 },
  lund: { lat: 55.7047, lng: 13.191, zoom: 12 },
  umea: { lat: 63.8258, lng: 20.263, zoom: 12 },
  gavle: { lat: 60.6749, lng: 17.1413, zoom: 12 },
  boras: { lat: 57.721, lng: 12.9401, zoom: 12 },
  sodertalje: { lat: 59.1955, lng: 17.6253, zoom: 12 },
  eskilstuna: { lat: 59.3666, lng: 16.5077, zoom: 12 },
  halmstad: { lat: 56.6745, lng: 12.8578, zoom: 12 },
  vaxjo: { lat: 56.8777, lng: 14.8094, zoom: 12 },
  karlstad: { lat: 59.4022, lng: 13.5115, zoom: 12 },
  sundsvall: { lat: 62.3908, lng: 17.3069, zoom: 12 },
  lulea: { lat: 65.5848, lng: 22.1547, zoom: 11 },
  trollhattan: { lat: 58.2837, lng: 12.2886, zoom: 12 },
  ostersund: { lat: 63.1792, lng: 14.6357, zoom: 12 },
  borlange: { lat: 60.4858, lng: 15.4371, zoom: 12 },
  kristianstad: { lat: 56.0294, lng: 14.1567, zoom: 12 },
  kalmar: { lat: 56.6616, lng: 16.3616, zoom: 12 },
  falun: { lat: 60.6065, lng: 15.6355, zoom: 12 },
  skelleftea: { lat: 64.7507, lng: 20.9527, zoom: 11 },
  karlskrona: { lat: 56.1612, lng: 15.5869, zoom: 12 },
  uddevalla: { lat: 58.3498, lng: 11.9424, zoom: 12 },
  nykoping: { lat: 58.7531, lng: 17.0086, zoom: 12 },
  motala: { lat: 58.5371, lng: 15.0359, zoom: 12 },
  varnamo: { lat: 57.1855, lng: 14.0407, zoom: 12 },
  visby: { lat: 57.6348, lng: 18.2948, zoom: 12 },
  gotland: { lat: 57.6348, lng: 18.2948, zoom: 10 },
  ornskoldsvik: { lat: 63.2884, lng: 18.7167, zoom: 12 },

  // ── A ──
  ale: { lat: 57.925, lng: 12.086, zoom: 12 },
  alingsas: { lat: 57.930, lng: 12.533, zoom: 12 },
  almhult: { lat: 56.551, lng: 14.138, zoom: 12 },
  alvdalen: { lat: 61.226, lng: 14.041, zoom: 11 },
  alvesta: { lat: 56.8989, lng: 14.5562, zoom: 12 },
  alvkarleby: { lat: 60.5667, lng: 17.45, zoom: 12 },
  alvsbyn: { lat: 65.674, lng: 20.963, zoom: 12 },
  amal: { lat: 59.052, lng: 12.705, zoom: 12 },
  aneby: { lat: 57.8392, lng: 14.8076, zoom: 12 },
  ange: { lat: 62.525, lng: 15.658, zoom: 11 },
  angelholm: { lat: 56.2428, lng: 12.8623, zoom: 12 },
  arboga: { lat: 59.394, lng: 15.843, zoom: 12 },
  are: { lat: 63.398, lng: 13.081, zoom: 11 },
  arjang: { lat: 59.39, lng: 12.13, zoom: 12 },
  arjeplog: { lat: 66.05, lng: 17.9, zoom: 10 },
  arvidsjaur: { lat: 65.59, lng: 19.17, zoom: 11 },
  arvika: { lat: 59.654, lng: 12.589, zoom: 12 },
  asele: { lat: 64.161, lng: 17.352, zoom: 11 },
  askersund: { lat: 58.877, lng: 14.902, zoom: 12 },
  astorp: { lat: 56.134, lng: 12.946, zoom: 12 },
  atvidaberg: { lat: 58.201, lng: 15.998, zoom: 12 },
  avesta: { lat: 60.146, lng: 16.173, zoom: 12 },

  // ── B ──
  bastad: { lat: 56.432, lng: 12.836, zoom: 12 },
  bengtsfors: { lat: 59.028, lng: 12.231, zoom: 12 },
  berg: { lat: 62.95, lng: 14.45, zoom: 11 },
  bjurholm: { lat: 63.931, lng: 19.215, zoom: 11 },
  bjuv: { lat: 56.083, lng: 12.917, zoom: 12 },
  boden: { lat: 65.825, lng: 21.689, zoom: 12 },
  bollebygd: { lat: 57.669, lng: 12.571, zoom: 12 },
  bollnas: { lat: 61.348, lng: 16.395, zoom: 12 },
  borgholm: { lat: 56.879, lng: 16.657, zoom: 12 },
  botkyrka: { lat: 59.2, lng: 17.8333, zoom: 12 },
  boxholm: { lat: 58.196, lng: 15.053, zoom: 12 },
  bracke: { lat: 62.750, lng: 15.420, zoom: 11 },
  bromolla: { lat: 56.0723, lng: 14.4748, zoom: 12 },
  burlov: { lat: 55.639, lng: 13.073, zoom: 13 },

  // ── D ──
  "dals-ed": { lat: 58.913, lng: 11.928, zoom: 11 },
  dals_ed: { lat: 58.913, lng: 11.928, zoom: 11 },
  danderyd: { lat: 59.414, lng: 18.035, zoom: 13 },
  degerfors: { lat: 59.237, lng: 14.430, zoom: 12 },
  dorotea: { lat: 64.265, lng: 16.4, zoom: 11 },

  // ── E ──
  eda: { lat: 59.854, lng: 12.316, zoom: 11 },
  ekero: { lat: 59.290, lng: 17.808, zoom: 12 },
  eksjo: { lat: 57.668, lng: 14.972, zoom: 12 },
  emmaboda: { lat: 56.629, lng: 15.541, zoom: 12 },
  enkoping: { lat: 59.6358, lng: 17.0775, zoom: 12 },
  eslov: { lat: 55.839, lng: 13.304, zoom: 12 },
  essunga: { lat: 58.188, lng: 12.784, zoom: 12 },

  // ── F ──
  fagersta: { lat: 59.9889, lng: 15.7989, zoom: 12 },
  falkenberg: { lat: 56.905, lng: 12.491, zoom: 12 },
  falkoping: { lat: 58.175, lng: 13.553, zoom: 12 },
  fargelanda: { lat: 58.567, lng: 11.996, zoom: 12 },
  filipstad: { lat: 59.712, lng: 14.168, zoom: 12 },
  finspang: { lat: 58.707, lng: 15.772, zoom: 12 },
  flen: { lat: 59.057, lng: 16.588, zoom: 12 },
  forshaga: { lat: 59.524, lng: 13.473, zoom: 12 },

  // ── G ──
  gagnef: { lat: 60.597, lng: 15.078, zoom: 12 },
  gallivare: { lat: 67.1335, lng: 20.6597, zoom: 11 },
  gislaved: { lat: 57.303, lng: 13.543, zoom: 12 },
  gnesta: { lat: 59.049, lng: 17.311, zoom: 12 },
  gnosjo: { lat: 57.359, lng: 13.725, zoom: 12 },
  gotene: { lat: 58.530, lng: 13.490, zoom: 12 },
  grastorp: { lat: 58.333, lng: 12.683, zoom: 12 },
  grums: { lat: 59.256, lng: 13.111, zoom: 12 },
  gullspang: { lat: 58.986, lng: 14.120, zoom: 12 },

  // ── H ──
  habo: { lat: 57.917, lng: 14.074, zoom: 12 },
  hagfors: { lat: 60.034, lng: 13.697, zoom: 12 },
  hallefors: { lat: 59.784, lng: 14.524, zoom: 12 },
  hallsberg: { lat: 59.0656, lng: 15.1102, zoom: 12 },
  hallstahammar: { lat: 59.615, lng: 16.229, zoom: 12 },
  hammaro: { lat: 59.31, lng: 13.5, zoom: 12 },
  haninge: { lat: 59.1689, lng: 18.1444, zoom: 12 },
  haparanda: { lat: 65.836, lng: 24.137, zoom: 12 },
  harjedalen: { lat: 62.45, lng: 13.6, zoom: 10 },
  harnosand: { lat: 62.6323, lng: 17.9379, zoom: 12 },
  harryda: { lat: 57.667, lng: 12.283, zoom: 12 },
  hassleholm: { lat: 56.1591, lng: 13.7666, zoom: 12 },
  heby: { lat: 59.943, lng: 16.877, zoom: 12 },
  hedemora: { lat: 60.278, lng: 15.987, zoom: 12 },
  herrljunga: { lat: 58.077, lng: 13.027, zoom: 12 },
  hjo: { lat: 58.301, lng: 14.286, zoom: 12 },
  hofors: { lat: 60.546, lng: 16.286, zoom: 12 },
  hoganas: { lat: 56.199, lng: 12.571, zoom: 12 },
  hogsby: { lat: 57.165, lng: 16.027, zoom: 12 },
  hoor: { lat: 55.932, lng: 13.544, zoom: 12 },
  horby: { lat: 55.852, lng: 13.6603, zoom: 12 },
  huddinge: { lat: 59.2367, lng: 17.9821, zoom: 12 },
  hudiksvall: { lat: 61.7274, lng: 17.105, zoom: 12 },
  hultsfred: { lat: 57.493, lng: 15.844, zoom: 12 },
  hylte: { lat: 57.000, lng: 13.243, zoom: 12 },

  // ── J ──
  jarfalla: { lat: 59.423, lng: 17.834, zoom: 13 },
  jokkmokk: { lat: 66.606, lng: 19.822, zoom: 10 },

  // ── K ──
  kalix: { lat: 65.854, lng: 23.141, zoom: 12 },
  karlsborg: { lat: 58.536, lng: 14.512, zoom: 12 },
  karlskoga: { lat: 59.327, lng: 14.524, zoom: 12 },
  katrineholm: { lat: 58.996, lng: 16.206, zoom: 12 },
  kavlinge: { lat: 55.793, lng: 13.111, zoom: 12 },
  kil: { lat: 59.5023, lng: 13.31, zoom: 12 },
  kinda: { lat: 57.988, lng: 15.626, zoom: 12 },
  kiruna: { lat: 67.855, lng: 20.225, zoom: 11 },
  klippan: { lat: 56.1378, lng: 13.1318, zoom: 12 },
  knivsta: { lat: 59.726, lng: 17.788, zoom: 12 },
  koping: { lat: 59.514, lng: 15.993, zoom: 12 },
  kramfors: { lat: 62.93, lng: 17.78, zoom: 12 },
  kristinehamn: { lat: 59.3097, lng: 14.1066, zoom: 12 },
  krokom: { lat: 63.326, lng: 14.453, zoom: 11 },
  kumla: { lat: 59.1287, lng: 15.1393, zoom: 12 },
  kungalv: { lat: 57.873, lng: 11.980, zoom: 12 },
  kungsbacka: { lat: 57.487, lng: 12.076, zoom: 12 },
  kungsor: { lat: 59.422, lng: 16.096, zoom: 12 },

  // ── L ──
  laholm: { lat: 56.512, lng: 13.042, zoom: 12 },
  landskrona: { lat: 55.870, lng: 12.830, zoom: 12 },
  laxa: { lat: 58.9817, lng: 14.6128, zoom: 12 },
  lekeberg: { lat: 59.170, lng: 14.872, zoom: 12 },
  leksand: { lat: 60.730, lng: 14.998, zoom: 12 },
  lerum: { lat: 57.770, lng: 12.270, zoom: 12 },
  lessebo: { lat: 56.751, lng: 15.263, zoom: 12 },
  lidingo: { lat: 59.366, lng: 18.140, zoom: 13 },
  lidkoping: { lat: 58.505, lng: 13.157, zoom: 12 },
  "lilla-edet": { lat: 58.134, lng: 12.131, zoom: 12 },
  lilla_edet: { lat: 58.134, lng: 12.131, zoom: 12 },
  lindesberg: { lat: 59.593, lng: 15.225, zoom: 12 },
  ljungby: { lat: 56.834, lng: 13.941, zoom: 12 },
  ljusdal: { lat: 61.83, lng: 16.092, zoom: 11 },
  ljusnarsberg: { lat: 59.873, lng: 14.996, zoom: 12 },
  lomma: { lat: 55.673, lng: 13.067, zoom: 13 },
  ludvika: { lat: 60.149, lng: 15.188, zoom: 12 },
  lycksele: { lat: 64.5934, lng: 18.6766, zoom: 11 },
  lysekil: { lat: 58.275, lng: 11.435, zoom: 12 },

  // ── M ──
  mala: { lat: 65.184, lng: 18.742, zoom: 11 },
  "malung-salen": { lat: 60.683, lng: 13.717, zoom: 10 },
  malung_salen: { lat: 60.683, lng: 13.717, zoom: 10 },
  mariestad: { lat: 58.710, lng: 13.823, zoom: 12 },
  mark: { lat: 57.489, lng: 12.695, zoom: 12 },
  markaryd: { lat: 56.4615, lng: 13.5957, zoom: 12 },
  mellerud: { lat: 58.700, lng: 12.450, zoom: 12 },
  mjolby: { lat: 58.324, lng: 15.131, zoom: 12 },
  molndal: { lat: 57.655, lng: 12.014, zoom: 12 },
  monsteras: { lat: 57.042, lng: 16.444, zoom: 12 },
  mora: { lat: 61.005, lng: 14.537, zoom: 12 },
  morbylanga: { lat: 56.524, lng: 16.382, zoom: 12 },
  mullsjo: { lat: 57.920, lng: 13.880, zoom: 12 },
  munkedal: { lat: 58.473, lng: 11.691, zoom: 12 },
  munkfors: { lat: 59.838, lng: 13.543, zoom: 12 },

  // ── N ──
  nacka: { lat: 59.3107, lng: 18.1647, zoom: 12 },
  nassjo: { lat: 57.653, lng: 14.697, zoom: 12 },
  nora: { lat: 59.519, lng: 15.039, zoom: 12 },
  norberg: { lat: 60.088, lng: 15.925, zoom: 12 },
  nordanstig: { lat: 61.984, lng: 17.070, zoom: 11 },
  nordmaling: { lat: 63.568, lng: 19.500, zoom: 11 },
  norrtalje: { lat: 59.7559, lng: 18.7036, zoom: 12 },
  norsjo: { lat: 64.913, lng: 19.481, zoom: 11 },
  nybro: { lat: 56.745, lng: 15.907, zoom: 12 },
  nykvarn: { lat: 59.176, lng: 17.432, zoom: 12 },
  nynashamn: { lat: 58.904, lng: 17.947, zoom: 12 },

  // ── O ──
  ockelbo: { lat: 60.887, lng: 16.717, zoom: 12 },
  ockero: { lat: 57.711, lng: 11.650, zoom: 12 },
  odeshog: { lat: 58.229, lng: 14.654, zoom: 12 },
  olofstrom: { lat: 56.2772, lng: 14.5325, zoom: 12 },
  orkelljunga: { lat: 56.284, lng: 13.330, zoom: 12 },
  orsa: { lat: 61.120, lng: 14.614, zoom: 12 },
  orust: { lat: 58.200, lng: 11.700, zoom: 11 },
  osby: { lat: 56.381, lng: 13.993, zoom: 12 },
  oskarshamn: { lat: 57.264, lng: 16.448, zoom: 12 },
  osteraker: { lat: 59.479, lng: 18.300, zoom: 12 },
  osthammar: { lat: 60.259, lng: 18.374, zoom: 12 },
  "ostra-goinge": { lat: 56.252, lng: 14.062, zoom: 12 },
  ostra_goinge: { lat: 56.252, lng: 14.062, zoom: 12 },
  ovanaker: { lat: 61.364, lng: 15.836, zoom: 11 },
  overkalix: { lat: 66.326, lng: 22.844, zoom: 11 },
  overtornea: { lat: 66.389, lng: 23.654, zoom: 11 },
  oxelosund: { lat: 58.670, lng: 17.106, zoom: 12 },

  // ── P ──
  pajala: { lat: 67.213, lng: 23.366, zoom: 10 },
  partille: { lat: 57.7394, lng: 12.1067, zoom: 12 },
  perstorp: { lat: 56.134, lng: 13.385, zoom: 12 },
  pitea: { lat: 65.317, lng: 21.480, zoom: 12 },

  // ── R ──
  ragunda: { lat: 63.111, lng: 16.363, zoom: 11 },
  rattvik: { lat: 60.886, lng: 15.118, zoom: 12 },
  robertsfors: { lat: 64.192, lng: 20.849, zoom: 11 },
  ronneby: { lat: 56.210, lng: 15.281, zoom: 12 },

  // ── S ──
  saffle: { lat: 59.130, lng: 12.929, zoom: 12 },
  sala: { lat: 59.9217, lng: 16.6035, zoom: 12 },
  salem: { lat: 59.200, lng: 17.767, zoom: 12 },
  sandviken: { lat: 60.6167, lng: 16.7667, zoom: 12 },
  sater: { lat: 60.348, lng: 15.748, zoom: 12 },
  savsjo: { lat: 57.402, lng: 14.663, zoom: 12 },
  sigtuna: { lat: 59.617, lng: 17.723, zoom: 12 },
  simrishamn: { lat: 55.556, lng: 14.351, zoom: 12 },
  sjobo: { lat: 55.635, lng: 13.705, zoom: 12 },
  skara: { lat: 58.387, lng: 13.4376, zoom: 12 },
  skinnskatteberg: { lat: 59.829, lng: 15.684, zoom: 12 },
  skovde: { lat: 58.390, lng: 13.845, zoom: 12 },
  skurup: { lat: 55.480, lng: 13.500, zoom: 12 },
  smedjebacken: { lat: 60.142, lng: 15.412, zoom: 12 },
  soderhamn: { lat: 61.3038, lng: 17.0593, zoom: 12 },
  soderkoping: { lat: 58.482, lng: 16.321, zoom: 12 },
  solleftea: { lat: 63.1672, lng: 17.2693, zoom: 12 },
  sollentuna: { lat: 59.429, lng: 17.951, zoom: 13 },
  solna: { lat: 59.36, lng: 18.0, zoom: 13 },
  solvesborg: { lat: 56.052, lng: 14.586, zoom: 12 },
  sorsele: { lat: 65.534, lng: 17.525, zoom: 10 },
  sotenas: { lat: 58.361, lng: 11.272, zoom: 12 },
  staffanstorp: { lat: 55.642, lng: 13.208, zoom: 12 },
  stenungsund: { lat: 58.070, lng: 11.819, zoom: 12 },
  storfors: { lat: 59.531, lng: 14.275, zoom: 12 },
  storuman: { lat: 65.096, lng: 17.114, zoom: 10 },
  strangnas: { lat: 59.377, lng: 17.031, zoom: 12 },
  stromstad: { lat: 58.938, lng: 11.176, zoom: 12 },
  stromsund: { lat: 63.85, lng: 15.5667, zoom: 11 },
  sundbyberg: { lat: 59.3617, lng: 17.9714, zoom: 13 },
  sunne: { lat: 59.838, lng: 13.144, zoom: 12 },
  surahammar: { lat: 59.707, lng: 16.218, zoom: 12 },
  svalov: { lat: 55.913, lng: 13.111, zoom: 12 },
  svedala: { lat: 55.507, lng: 13.236, zoom: 12 },
  svenljunga: { lat: 57.496, lng: 13.111, zoom: 12 },

  // ── T ──
  taby: { lat: 59.444, lng: 18.069, zoom: 13 },
  tanum: { lat: 58.723, lng: 11.324, zoom: 11 },
  tibro: { lat: 58.419, lng: 14.162, zoom: 12 },
  tidaholm: { lat: 58.181, lng: 13.958, zoom: 12 },
  tierp: { lat: 60.342, lng: 17.5167, zoom: 12 },
  timra: { lat: 62.487, lng: 17.32, zoom: 12 },
  tingsryd: { lat: 56.529, lng: 14.975, zoom: 12 },
  tjorn: { lat: 58.000, lng: 11.600, zoom: 12 },
  tomelilla: { lat: 55.549, lng: 13.954, zoom: 12 },
  toreboda: { lat: 58.706, lng: 14.127, zoom: 12 },
  torsas: { lat: 56.411, lng: 15.998, zoom: 12 },
  torsby: { lat: 60.137, lng: 13.008, zoom: 11 },
  tranas: { lat: 58.037, lng: 14.977, zoom: 12 },
  tranemo: { lat: 57.488, lng: 13.353, zoom: 12 },
  trelleborg: { lat: 55.3753, lng: 13.1569, zoom: 12 },
  trosa: { lat: 58.898, lng: 17.550, zoom: 12 },
  tyreso: { lat: 59.245, lng: 18.230, zoom: 13 },

  // ── U ──
  ulricehamn: { lat: 57.791, lng: 13.414, zoom: 12 },
  "upplands-bro": { lat: 59.518, lng: 17.653, zoom: 12 },
  upplands_bro: { lat: 59.518, lng: 17.653, zoom: 12 },
  "upplands-vasby": { lat: 59.519, lng: 17.910, zoom: 13 },
  upplands_vasby: { lat: 59.519, lng: 17.910, zoom: 13 },
  uppvidinge: { lat: 57.000, lng: 15.300, zoom: 11 },

  // ── V ──
  vadstena: { lat: 58.448, lng: 14.890, zoom: 12 },
  vaggeryd: { lat: 57.500, lng: 14.150, zoom: 12 },
  valdemarsvik: { lat: 58.204, lng: 16.602, zoom: 12 },
  vallentuna: { lat: 59.533, lng: 18.078, zoom: 12 },
  vanersborg: { lat: 58.380, lng: 12.324, zoom: 12 },
  vannas: { lat: 63.908, lng: 19.754, zoom: 12 },
  vansbro: { lat: 60.510, lng: 14.225, zoom: 12 },
  vara: { lat: 58.261, lng: 12.956, zoom: 12 },
  varberg: { lat: 57.106, lng: 12.253, zoom: 12 },
  vargarda: { lat: 58.033, lng: 12.809, zoom: 12 },
  varmdo: { lat: 59.330, lng: 18.400, zoom: 12 },
  vastervik: { lat: 57.7575, lng: 16.6376, zoom: 12 },
  vaxholm: { lat: 59.403, lng: 18.353, zoom: 13 },
  vellinge: { lat: 55.471, lng: 13.018, zoom: 12 },
  vetlanda: { lat: 57.427, lng: 15.086, zoom: 12 },
  vilhelmina: { lat: 64.627, lng: 16.656, zoom: 11 },
  vimmerby: { lat: 57.6678, lng: 15.8569, zoom: 12 },
  vindeln: { lat: 64.200, lng: 19.717, zoom: 11 },
  vingaker: { lat: 59.043, lng: 15.872, zoom: 12 },

  // ── Y ──
  ydre: { lat: 57.828, lng: 15.275, zoom: 11 },
  ystad: { lat: 55.4297, lng: 13.8204, zoom: 12 },
};

// Fallback center: roughly Sweden's geographic center
const SWEDEN_FALLBACK: CityCenter = { lat: 62.0, lng: 15.0, zoom: 5 };

export function getCityCenter(slug: string): CityCenter {
  if (!slug) return SWEDEN_FALLBACK;
  const normalizedSlug = slug.toLowerCase().trim();
  if (CITY_COORDS[normalizedSlug]) return CITY_COORDS[normalizedSlug];
  const withHyphen = normalizedSlug.replace(/_/g, "-");
  if (CITY_COORDS[withHyphen]) return CITY_COORDS[withHyphen];
  const withUnderscore = normalizedSlug.replace(/-/g, "_");
  if (CITY_COORDS[withUnderscore]) return CITY_COORDS[withUnderscore];
  return SWEDEN_FALLBACK;
}

// Display names for slugs that lose Swedish characters (Å, Ä, Ö) when
// slugified. We use this anywhere we want to show a slug as a city label
// without losing diacritics.
const CITY_DISPLAY_NAMES: Record<string, string> = {
  alingsas: "Alingsås",
  almhult: "Älmhult",
  alvdalen: "Älvdalen",
  alvesta: "Alvesta",
  alvkarleby: "Älvkarleby",
  alvsbyn: "Älvsbyn",
  amal: "Åmål",
  ange: "Ånge",
  angelholm: "Ängelholm",
  are: "Åre",
  arjang: "Årjäng",
  asele: "Åsele",
  astorp: "Åstorp",
  atvidaberg: "Åtvidaberg",
  bastad: "Båstad",
  bollnas: "Bollnäs",
  boras: "Borås",
  borlange: "Borlänge",
  bracke: "Bräcke",
  bromolla: "Bromölla",
  burlov: "Burlöv",
  "dals-ed": "Dals-Ed",
  dals_ed: "Dals-Ed",
  ekero: "Ekerö",
  eksjo: "Eksjö",
  enkoping: "Enköping",
  eslov: "Eslöv",
  falkoping: "Falköping",
  fargelanda: "Färgelanda",
  finspang: "Finspång",
  gallivare: "Gällivare",
  gavle: "Gävle",
  gnosjo: "Gnosjö",
  goteborg: "Göteborg",
  gotene: "Götene",
  grastorp: "Grästorp",
  gullspang: "Gullspång",
  habo: "Habo / Håbo",
  hallefors: "Hällefors",
  hallstahammar: "Hallstahammar",
  hammaro: "Hammarö",
  harjedalen: "Härjedalen",
  harnosand: "Härnösand",
  harryda: "Härryda",
  hassleholm: "Hässleholm",
  hoganas: "Höganäs",
  hogsby: "Högsby",
  hoor: "Höör",
  horby: "Hörby",
  jarfalla: "Järfälla",
  jonkoping: "Jönköping",
  kavlinge: "Kävlinge",
  koping: "Köping",
  kungalv: "Kungälv",
  kungsor: "Kungsör",
  laxa: "Laxå",
  lidingo: "Lidingö",
  lidkoping: "Lidköping",
  "lilla-edet": "Lilla Edet",
  lilla_edet: "Lilla Edet",
  linkoping: "Linköping",
  lulea: "Luleå",
  mala: "Malå",
  malmo: "Malmö",
  "malung-salen": "Malung-Sälen",
  malung_salen: "Malung-Sälen",
  mjolby: "Mjölby",
  molndal: "Mölndal",
  monsteras: "Mönsterås",
  morbylanga: "Mörbylånga",
  mullsjo: "Mullsjö",
  nassjo: "Nässjö",
  norrkoping: "Norrköping",
  norrtalje: "Norrtälje",
  norsjo: "Norsjö",
  nykoping: "Nyköping",
  nynashamn: "Nynäshamn",
  ockero: "Öckerö",
  odeshog: "Ödeshög",
  olofstrom: "Olofström",
  orebro: "Örebro",
  orkelljunga: "Örkelljunga",
  ornskoldsvik: "Örnsköldsvik",
  osteraker: "Österåker",
  ostersund: "Östersund",
  osthammar: "Östhammar",
  "ostra-goinge": "Östra Göinge",
  ostra_goinge: "Östra Göinge",
  ovanaker: "Ovanåker",
  overkalix: "Överkalix",
  overtornea: "Övertorneå",
  oxelosund: "Oxelösund",
  pitea: "Piteå",
  rattvik: "Rättvik",
  ronneby: "Ronneby",
  saffle: "Säffle",
  sater: "Säter",
  savsjo: "Sävsjö",
  sjobo: "Sjöbo",
  skelleftea: "Skellefteå",
  skovde: "Skövde",
  soderhamn: "Söderhamn",
  soderkoping: "Söderköping",
  sodertalje: "Södertälje",
  solleftea: "Sollefteå",
  solvesborg: "Sölvesborg",
  sotenas: "Sotenäs",
  strangnas: "Strängnäs",
  stromstad: "Strömstad",
  stromsund: "Strömsund",
  svalov: "Svalöv",
  taby: "Täby",
  timra: "Timrå",
  tjorn: "Tjörn",
  toreboda: "Töreboda",
  torsas: "Torsås",
  tranas: "Tranås",
  trollhattan: "Trollhättan",
  tyreso: "Tyresö",
  umea: "Umeå",
  "upplands-bro": "Upplands-Bro",
  upplands_bro: "Upplands-Bro",
  "upplands-vasby": "Upplands Väsby",
  upplands_vasby: "Upplands Väsby",
  vanersborg: "Vänersborg",
  vannas: "Vännäs",
  vargarda: "Vårgårda",
  varmdo: "Värmdö",
  varnamo: "Värnamo",
  vasteras: "Västerås",
  vastervik: "Västervik",
  vaxjo: "Växjö",
  vingaker: "Vingåker",
};

export function getCityName(slug: string): string {
  if (CITY_DISPLAY_NAMES[slug]) return CITY_DISPLAY_NAMES[slug];
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

// Approximate population (tätort) per city slug. Used to compute a rough
// "brott per 1000 invånare" indicator on the cities list and city detail
// pages. Numbers only need to be in the right ballpark.
const CITY_POPULATION: Record<string, number> = {
  ale: 32581,
  alingsas: 42869,
  almhult: 17549,
  alvdalen: 6792,
  alvesta: 19692,
  alvkarleby: 9584,
  alvsbyn: 7772,
  amal: 11592,
  aneby: 6857,
  ange: 8945,
  angelholm: 45138,
  arboga: 13886,
  are: 12792,
  arjang: 9714,
  arjeplog: 2566,
  arvidsjaur: 6016,
  arvika: 25374,
  asele: 2665,
  askersund: 11404,
  astorp: 16520,
  atvidaberg: 11457,
  avesta: 22294,
  bastad: 16036,
  bengtsfors: 8939,
  berg: 7086,
  bjurholm: 2321,
  bjuv: 16086,
  boden: 28795,
  bollebygd: 9810,
  bollnas: 25997,
  boras: 115177,
  borgholm: 10628,
  borlange: 51415,
  botkyrka: 96996,
  boxholm: 5457,
  bracke: 5956,
  bromolla: 12490,
  burlov: 20454,
  "dals-ed": 4580,
  danderyd: 32400,
  degerfors: 9213,
  dorotea: 2221,
  eda: 8287,
  ekero: 29063,
  eksjo: 17648,
  emmaboda: 8786,
  enkoping: 48811,
  eskilstuna: 106694,
  eslov: 35079,
  essunga: 5544,
  fagersta: 12887,
  falkenberg: 47309,
  falkoping: 32814,
  falun: 59917,
  fargelanda: 6262,
  filipstad: 9598,
  finspang: 21468,
  flen: 15039,
  forshaga: 11404,
  gagnef: 10280,
  gallivare: 17108,
  gavle: 104160,
  gislaved: 28590,
  gnesta: 11308,
  gnosjo: 9008,
  goteborg: 613685,
  gotene: 13227,
  gotland: 60796,
  grastorp: 5587,
  grums: 8861,
  gullspang: 4949,
  habo: 23233,
  hagfors: 11335,
  hallefors: 6266,
  hallsberg: 16047,
  hallstahammar: 16614,
  halmstad: 106202,
  hammaro: 16902,
  haninge: 101536,
  haparanda: 8998,
  harjedalen: 10077,
  harnosand: 24300,
  harryda: 39965,
  hassleholm: 51866,
  heby: 14256,
  hedemora: 14990,
  helsingborg: 152762,
  herrljunga: 9468,
  hjo: 9300,
  hofors: 9118,
  hoganas: 28412,
  hogsby: 5099,
  hoor: 17664,
  horby: 15502,
  huddinge: 114889,
  hudiksvall: 37302,
  hultsfred: 13432,
  hylte: 10061,
  jarfalla: 90397,
  jokkmokk: 4691,
  jonkoping: 148301,
  kalix: 15255,
  kalmar: 72980,
  karlsborg: 7060,
  karlshamn: 31598,
  karlskoga: 30143,
  karlskrona: 66012,
  karlstad: 98996,
  katrineholm: 33946,
  kavlinge: 32711,
  kil: 12026,
  kinda: 9907,
  kiruna: 22412,
  klippan: 17660,
  knivsta: 21834,
  koping: 25578,
  kramfors: 17287,
  kristianstad: 85852,
  kristinehamn: 23574,
  krokom: 15820,
  kumla: 22723,
  kungalv: 50453,
  kungsbacka: 86399,
  kungsor: 8549,
  laholm: 26480,
  landskrona: 47626,
  laxa: 5385,
  lekeberg: 8597,
  leksand: 16178,
  lerum: 43673,
  lessebo: 8064,
  lidingo: 48277,
  lidkoping: 40341,
  "lilla-edet": 14411,
  lindesberg: 22976,
  linkoping: 168796,
  ljungby: 28108,
  ljusdal: 18343,
  ljusnarsberg: 4301,
  lomma: 24645,
  ludvika: 26869,
  lulea: 80455,
  lund: 132512,
  lycksele: 12147,
  lysekil: 13760,
  mala: 2898,
  malmo: 368287,
  "malung-salen": 10211,
  mariestad: 24364,
  mark: 35081,
  markaryd: 9846,
  mellerud: 9037,
  mjolby: 28855,
  molndal: 72314,
  monsteras: 12968,
  mora: 20443,
  morbylanga: 16319,
  motala: 43075,
  mullsjo: 7611,
  munkedal: 10242,
  munkfors: 3643,
  nacka: 113873,
  nassjo: 31444,
  nora: 10552,
  norberg: 5336,
  nordanstig: 9282,
  nordmaling: 6915,
  norrkoping: 144901,
  norrtalje: 66858,
  norsjo: 3789,
  nybro: 19811,
  nykoping: 58255,
  nykvarn: 12342,
  nynashamn: 30468,
  ockelbo: 5710,
  ockero: 12845,
  odeshog: 5224,
  olofstrom: 12778,
  orebro: 160691,
  orkelljunga: 10282,
  ornskoldsvik: 55350,
  orsa: 6862,
  orust: 15355,
  osby: 12829,
  oskarshamn: 26682,
  osteraker: 49853,
  ostersund: 65086,
  osthammar: 22151,
  "ostra-goinge": 13707,
  ovanaker: 11248,
  overkalix: 3175,
  overtornea: 3979,
  oxelosund: 11959,
  pajala: 5680,
  partille: 41133,
  perstorp: 7083,
  pitea: 42191,
  ragunda: 5139,
  rattvik: 10962,
  robertsfors: 6606,
  ronneby: 28638,
  saffle: 14717,
  sala: 22585,
  salem: 17346,
  sandviken: 38040,
  sater: 11245,
  savsjo: 11494,
  sigtuna: 53414,
  simrishamn: 18796,
  sjobo: 19371,
  skara: 18680,
  skelleftea: 75051,
  skinnskatteberg: 4210,
  skovde: 58300,
  skurup: 17124,
  smedjebacken: 10770,
  soderhamn: 24087,
  soderkoping: 14700,
  sodertalje: 102784,
  solleftea: 18069,
  sollentuna: 78157,
  solna: 86464,
  solvesborg: 17380,
  sorsele: 2376,
  sotenas: 8994,
  staffanstorp: 27515,
  stenungsund: 27867,
  stockholm: 999713,
  storfors: 3768,
  storuman: 5555,
  strangnas: 39398,
  stromstad: 13565,
  stromsund: 10883,
  sundbyberg: 57197,
  sundsvall: 98886,
  sunne: 13317,
  surahammar: 9728,
  svalov: 14449,
  svedala: 23690,
  svenljunga: 10597,
  taby: 78547,
  tanum: 12646,
  tibro: 11296,
  tidaholm: 12681,
  tierp: 21013,
  timra: 17418,
  tingsryd: 11863,
  tjorn: 15995,
  tomelilla: 13633,
  toreboda: 9078,
  torsas: 6899,
  torsby: 11258,
  tranas: 18510,
  tranemo: 11864,
  trelleborg: 47440,
  trollhattan: 59162,
  trosa: 14894,
  tyreso: 49710,
  uddevalla: 56698,
  ulricehamn: 24973,
  umea: 135390,
  "upplands-bro": 33482,
  "upplands-vasby": 50610,
  uppsala: 249896,
  uppvidinge: 8897,
  vadstena: 7474,
  vaggeryd: 14971,
  valdemarsvik: 7534,
  vallentuna: 35375,
  vanersborg: 40131,
  vannas: 9193,
  vansbro: 6729,
  vara: 15995,
  varberg: 69502,
  vargarda: 12500,
  varmdo: 46486,
  varnamo: 34502,
  vasteras: 161229,
  vastervik: 36221,
  vaxholm: 11668,
  vaxjo: 98814,
  vellinge: 37785,
  vetlanda: 27374,
  vilhelmina: 6170,
  vimmerby: 15307,
  vindeln: 5396,
  vingaker: 8633,
  ydre: 3599,
  ystad: 32188,
};

export function getCityPopulation(slug: string): number | null {
  return CITY_POPULATION[slug] ?? null;
}

/**
 * Alla kända kommun-slugs i klientbundlen.
 * Baseras på Sveriges samtliga kommuner i CITY_POPULATION så att
 * alla 290 kommuner alltid inkluderas i sök, listor och Trygghetsindex.
 */
export function getAllCitySlugs(): string[] {
  return Object.keys(CITY_POPULATION);
}

export interface CityListEntry {
  slug: string;
  name: string;
}

export function getAllCities(): CityListEntry[] {
  return getAllCitySlugs()
    .map((slug) => ({ slug, name: getCityName(slug) }))
    .sort((a, b) => a.name.localeCompare(b.name, "sv"));
}

let cachedNormalizedMunicipalities: Set<string> | null = null;

function normalizeNameForComparison(s: string): string {
  return s
    .toLowerCase()
    .replace(/å/g, "a")
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/é/g, "e")
    .replace(/ü/g, "u")
    .replace(/[.,;:!?()"'']/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getNormalizedMunicipalitiesSet(): Set<string> {
  if (cachedNormalizedMunicipalities) return cachedNormalizedMunicipalities;
  const set = new Set<string>();

  for (const slug of Object.keys(CITY_POPULATION)) {
    const rawSlug = slug.toLowerCase();
    set.add(rawSlug);
    set.add(rawSlug.replace(/[-_]/g, " "));
    set.add(rawSlug.replace(/[-_]/g, ""));
    const name = getCityName(slug);
    if (name) {
      const lowerName = name.toLowerCase().trim();
      set.add(lowerName);
      set.add(normalizeNameForComparison(lowerName));
    }
  }

  for (const [slug, name] of Object.entries(CITY_DISPLAY_NAMES)) {
    const rawSlug = slug.toLowerCase();
    set.add(rawSlug);
    set.add(rawSlug.replace(/[-_]/g, " "));
    set.add(rawSlug.replace(/[-_]/g, ""));
    const lowerName = name.toLowerCase().trim();
    set.add(lowerName);
    set.add(normalizeNameForComparison(lowerName));
  }

  cachedNormalizedMunicipalities = set;
  return set;
}

/**
 * Kontrollerar om en sträng enbart är ett svenskt kommunnamn (t.ex. "Kungälv", "Kungälvs kommun", "Göteborg")
 * och INTE ett specifikt gatunamn/adress/plats (t.ex. "Kungälvsgatan", "Kungsgatan 4").
 */
export function isMunicipalityOrCityName(input?: string | null): boolean {
  if (!input) return false;
  let s = input.trim();
  if (s.length < 2) return false;

  // Om strängen innehåller siffror/husnummer eller tydliga väg-suffix är det en gata/adress
  if (/\d+/.test(s)) return false;
  if (
    /(?:gatan|vägen|vagen|gränd|grand|allén|allen|leden|stigen|backen|stråket|straket|torget|platsen|planen|avenyn|boulevard|kajen|promenaden|bron|viadukten|tunneln)\b/i.test(
      s,
    )
  ) {
    return false;
  }

  // Rensa vanliga prefix/suffix (t.ex. "i Kungälvs kommun" -> "Kungälv")
  s = s
    .replace(
      /^(?:i|på|pa|vid|utanför|utanfor|nära|nara|centrala|södra|sodra|norra|östra|ostra|västra|vastra)\s+/i,
      "",
    )
    .replace(/\s+(?:kommun|stad|län|lan)$/i, "")
    .replace(/s\s+(?:kommun|stad|län|lan)$/i, "")
    .replace(/[.,;:!?()"'']/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!s) return false;

  const set = getNormalizedMunicipalitiesSet();
  const lower = s.toLowerCase();
  const normalized = normalizeNameForComparison(s);
  const withoutHyphens = lower.replace(/[-_]/g, " ");
  const collapsed = lower.replace(/[-_\s]/g, "");

  if (
    set.has(lower) ||
    set.has(normalized) ||
    set.has(withoutHyphens) ||
    set.has(collapsed)
  ) {
    return true;
  }

  // Genitiv-form (t.ex. "Kungälvs" -> "Kungälv")
  if (lower.endsWith("s") && lower.length > 3) {
    const withoutS = lower.slice(0, -1);
    const normWithoutS = normalizeNameForComparison(withoutS);
    if (set.has(withoutS) || set.has(normWithoutS)) {
      return true;
    }
  }

  return false;
}
